import { Box, Typography, Grid } from "@mui/joy";
import { LogIn } from "lucide-react";
import { Controller, useFormContext } from "react-hook-form";
import { Switch as JoySwitch } from "@mui/joy";
import { useNavigate } from "react-router-dom";

import { TextField, Textarea, Button } from "../../../../../shared";
import { useOrganizationImpersonation } from "../../../../../hooks";
import { LogoUploadField } from "../../LogoUploadField";

type OrganizationConfigurationProps = {
    organizationId?: string;
    isActive?: boolean;
};

export function OrganizationConfiguration({
    organizationId,
    isActive,
}: OrganizationConfigurationProps) {
    const { control } = useFormContext();
    const navigate = useNavigate();
    const { startImpersonation, isStarting } = useOrganizationImpersonation();
    const canImpersonate = !!organizationId && !!isActive;

    const handleImpersonate = async () => {
        if (!canImpersonate) return;
        try {
            await startImpersonation(organizationId);
            navigate("/");
        } catch {
            // Error snackbar is shown by useOrganizationImpersonation
        }
    };

    return (
        <Box flex={1}>
            <Box mt={2} display="flex" flexDirection="column" gap={1.5}>
                <Box display="flex" alignItems="flex-start" gap={2}>
                    <Box flex={1} minWidth={0}>
                        <LogoUploadField />
                    </Box>
                    {organizationId ? (
                        <Button
                            type="button"
                            size="sm"
                            variant="soft"
                            color="primary"
                            startDecorator={<LogIn size={14} />}
                            loading={isStarting}
                            disabled={!canImpersonate}
                            onClick={handleImpersonate}
                            sx={{ borderRadius: "4px", flexShrink: 0, mt: 0.5 }}
                        >
                            Impersonate
                        </Button>
                    ) : null}
                </Box>

                <TextField
                    name="Name"
                    label="Organization Name"
                    helperText="Enter the name of the organization"
                    placeholder="e.g., Colorado Roofing Company"
                />

                <TextField
                    name="ShortName"
                    label="Short Name"
                    helperText="Optional short name or abbreviation"
                    placeholder="e.g., CRC"
                />

                <Textarea
                    name="Description"
                    label="Description"
                    helperText="Optional summary of the organization"
                    placeholder="e.g., Regional roofing contractor serving Colorado homeowners"
                    minRows={3}
                />

                <Grid container spacing={2}>
                    <Grid xs={12}>
                        <TextField
                            name="Website"
                            label="Website"
                            helperText="Optional organization website"
                            placeholder="e.g., https://www.example.com"
                        />
                    </Grid>
                </Grid>

                <Box mt={1}>
                    <Typography level="body-sm" sx={{ mb: 1, fontWeight: 500 }}>
                        Active Status
                    </Typography>
                    <Controller
                        name="IsActive"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                            <JoySwitch
                                checked={!!value}
                                onChange={(event) => onChange(event.target.checked)}
                                sx={{
                                    "--Switch-thumbSize": "16px",
                                    "--Switch-trackWidth": "36px",
                                    "--Switch-trackHeight": "20px",
                                }}
                            />
                        )}
                    />
                </Box>
            </Box>
        </Box>
    );
}
